import { FC, memo } from 'react'
import get from 'lodash/get'
import { useTranslation } from '../../../hooks/locale'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import NextImage from '../next-image'
import WishlistSection from '../wishlist-section'
import { stopPropagation } from '../../../utils/dom-utils'
import { preventDefaultHandler } from '../../utils'
import StarsContainerComponent from '../stars-container'
import {
    CardContainer,
    CardContent,
    LocationText,
    TitleLink,
    PropertyTitle,
    PropertyDetails,
    RatingRow,
    RatingContainer,
    RatingValue,
    RatingCount,
    ImageSection,
    ImageContainer,
    CompactFavoriteButton,
    BottomSection,
    PriceRow,
    PriceText,
    FreeCancellationContainer,
    FreeCancellationText,
    GuestInfoText,
    BottomTraumTagRow,
    SlasherPrice,
} from './styles'
import {
    LocationContainer,
    BaseSeoHref as SeoHref,
    BaseFreeCancellationIcon as FreeCancellationIcon,
    BottomTraumTagImage,
    BottomTraumTagText,
} from '../card-shared-styles'
import { useCardData } from '../../../hooks/use-card-data'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import { useABVariant } from '../../../utils/hooks'

interface Props {
    listingObject: ListingSectionObject
    pageName: string
    sectionName: string
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
    index: number
}

const MAX_STARS = 5

const CompactCard: FC<Props> = ({
    listingObject,
    pageName,
    sectionName,
    breadcrumbModel,
    offerModel,
    suitablesModel,
    index,
}) => {
    const { t } = useTranslation()

    const { isProvidedVariantActive: isSlasherPriceActive } = useABVariant(AB_KEY.SLASHER_PRICE, AB_VARIANT_VERSION.ONE)

    const cardData = useCardData({
        index,
        listingObject,
        pageName,
        sectionName,
        breadcrumbModel,
        offerModel,
        suitablesModel,
    })

    const {
        propertyTitle,
        propertyDescriptionText,
        regionName,
        regionLink,
        propertyImage,
        priceValue,
        priceInfoValue,
        isFreeCancellation,
        shouldShowRating,
        formattedRatingAverage,
        formattedRatingCount,
        traumTagsData,
        handleCardClick,
        intersectionTargetRef,
        priceBeforeOffer,
        href,
        seoHref,
        noCrawl,
    } = cardData

    return (
        <CardContainer
            ref={intersectionTargetRef}
            onClick={handleCardClick}
            $hasBottomTags={traumTagsData.bottomTraumTags.length > 0}
        >
            <CardContent>
                <LocationContainer>
                    <SeoHref title={regionName} href={regionLink ?? undefined} onClick={stopPropagation}>
                        <LocationText>{regionName}</LocationText>
                    </SeoHref>
                </LocationContainer>
                <TitleLink
                    autoRedirect={false}
                    title={propertyTitle}
                    href={seoHref ?? href}
                    onClick={preventDefaultHandler}
                    noCrawl={noCrawl ?? false}
                >
                    <PropertyTitle>{propertyTitle}</PropertyTitle>
                </TitleLink>
                {!!propertyDescriptionText && <PropertyDetails>{propertyDescriptionText}</PropertyDetails>}
                {shouldShowRating && (
                    <RatingRow>
                        <RatingContainer>
                            <RatingValue>{formattedRatingAverage}</RatingValue>
                            <StarsContainerComponent
                                averageRating={Number(formattedRatingAverage)}
                                maxStars={MAX_STARS}
                            />
                            <RatingCount>{formattedRatingCount}</RatingCount>
                        </RatingContainer>
                    </RatingRow>
                )}
            </CardContent>
            <ImageSection>
                <ImageContainer>
                    <NextImage
                        containerComponent={ImageContainer}
                        src={get(propertyImage, 'url', '')}
                        alt={get(propertyImage, 'alt', 'Property image')}
                        objectFit="cover"
                        imwidth={360}
                    />
                </ImageContainer>
                <CompactFavoriteButton onClick={stopPropagation}>
                    <WishlistSection
                        containerBorderRadius="1.456rem"
                        containerHeight="3rem"
                        containerWidth="3rem"
                        containerMargin="0"
                        containerPadding="0"
                        width="1.5rem"
                        height="1.5rem"
                        listingId={listingObject.id?.toString() || ''}
                        showBoxShadow={false}
                        strokeColor="white"
                        containerBackgroundColor="transparent"
                    />
                </CompactFavoriteButton>
            </ImageSection>
            <BottomSection>
                <PriceRow>
                    {isSlasherPriceActive && !!priceBeforeOffer && <SlasherPrice>{priceBeforeOffer}</SlasherPrice>}
                    <PriceText>{priceValue}</PriceText>
                    {isFreeCancellation && (
                        <FreeCancellationContainer>
                            <FreeCancellationIcon />
                            <FreeCancellationText>{t('freeCancellation')}</FreeCancellationText>
                        </FreeCancellationContainer>
                    )}
                </PriceRow>
                {!!priceInfoValue && <GuestInfoText>{priceInfoValue}</GuestInfoText>}
                {traumTagsData.bottomTraumTags.map(tag => (
                    <BottomTraumTagRow key={tag.id}>
                        {tag.imageUrl && <BottomTraumTagImage src={tag.imageUrl} alt={t(tag.label)} />}
                        <BottomTraumTagText>{t(tag.label)}</BottomTraumTagText>
                    </BottomTraumTagRow>
                ))}
            </BottomSection>
        </CardContainer>
    )
}

export default memo(CompactCard)
